import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Close,
  ErrorOutline,
  BuildCircle,
  InfoOutlined,
} from '@mui/icons-material';
import { DangerHexagon,DangerTriangle,CheckCircle } from "@mynaui/icons-react";
import {getHealthCheckData} from '../data/healthCheckMockData';


const HealthCheckModal = ({ open, onClose, assetId, onHealthCheckComplete, zoneId }) => {
  const [stage, setStage] = useState('checking'); // checking, diagnosing, result
  const [scenario, setScenario] = useState(null);
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);

  useEffect(() => {
    if (open && assetId) {
      // Reset state when modal opens
      setStage('checking');
      setNotes('');
      setConfirmed(false);
      setShowAiAnalysis(false);

      const data = getHealthCheckData(assetId, zoneId);
      setHealthData(data);
      setScenario(data.scenario);

      // Pre-populate notes with approval request summary
      if (data.scenario === 'failure' && data.aiAnalysisDetails) {
        const details = data.aiAnalysisDetails;
        const rootCause = details.findings?.find(f => f.label === 'Root Cause')?.value || data.diagnosis.title;
        const severity = details.findings?.find(f => f.label === 'Severity')?.value || 'Requires attention';
        const warnings = details.aiNotes?.filter(n => n.type === 'warning').map(n => n.text) || [];

        const lines = [
          `Issue: ${rootCause}`,
          `Severity: ${severity}`,
          // `Remaining Useful Life: ${rul}`,
          // `AI Confidence: ${details.confidence}%`,
        ];
        if (warnings.length) {
          lines.push('', 'Safety Precautions:', ...warnings.map((w, i) => `${i + 1}. ${w}`));
        }
        setNotes(lines.join('\n'));
      }

      // Simulate loading stages
      const timer1 = setTimeout(() => {
        setStage('diagnosing');
      }, 2000);

      const timer2 = setTimeout(() => {
        setStage('result');
      }, 4000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [open, assetId, zoneId]);

  const handleClose = () => {
    setStage('checking');
    setNotes('');
    setConfirmed(false);
    setShowAiAnalysis(false);
    onClose();
  };

  const handleRequestApproval = () => {
    console.log('Request approval:', { assetId, notes, confirmed });
    // Trigger parts availability check
    handleClose();
    if (onHealthCheckComplete) {
      onHealthCheckComplete(healthData);
    }
  };

  const handleSubmitIncorrect = () => {
    console.log('Submit incorrect assessment:', { assetId, notes, confirmed });
    // Here you would send the feedback to your backend
    handleClose();
  };

  const renderLoadingScreen = (title, subtitle) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        py: 6,
      }}
    >
      <CircularProgress size={60} sx={{ mb: 3, color: '#2E7D32' }} />
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: '#757575' }}>
        {subtitle}
      </Typography>
    </Box>
  );

  const renderHealthyResult = () => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 4,
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <CheckCircle  style={{ fontSize:48, color: '#2E7D32' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {healthData?.diagnosis.title}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: '#757575', mb: 3, textAlign: 'center' }}
        >
          {healthData?.diagnosis.description}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setShowAiAnalysis(true)}
          sx={{
            color: '#1976D2',
            borderColor: '#1976D2',
            textTransform: 'none',
            mb: 4,
            '&:hover': {
              borderColor: '#1565C0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          {healthData?.diagnosis.aiAnalysis}
        </Button>
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={handleClose}
        sx={{
          backgroundColor: '#212121',
          color: '#fff',
          textTransform: 'none',
          fontWeight: 600,
          py: 1.5,
          '&:hover': {
            backgroundColor: '#000',
          },
        }}
      >
        ← Go back
      </Button>
    </Box>
  );

  const renderFailureResult = () => (
    <Box>
      <IconButton
        onClick={handleClose}
        sx={{ position: 'absolute', right: 16, top: 16 }}
      >
        <Close />
      </IconButton>

      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
            <DangerHexagon style={{ fontSize: 48, color:'#C62828' }} />
          {/* <Warning sx={{ fontSize: 50, color: '#C62828' }} /> */}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {healthData?.diagnosis.title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#757575', mb: 1 }}>
          {healthData?.diagnosis.description}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setShowAiAnalysis(true)}
          sx={{
            color: '#1976D2',
            borderColor: '#1976D2',
            textTransform: 'none',
            fontSize: '0.875rem',
            '&:hover': {
              borderColor: '#1565C0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          {healthData?.diagnosis.aiAnalysis}
        </Button>
      </Box>

      <Alert
        icon={<DangerTriangle style={{ fontSize: 50, color:'#E65100' }} />}
        sx={{
          backgroundColor: '#FFF3E0',
          border: '1px solid #FFE0B2',
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          '& .MuiAlert-message': {
            color: '#E65100',
            fontSize: '0.875rem',
          },
        }}
      >
        {healthData?.diagnosis.warning}
      </Alert>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ color: '#424242', mb: 2 }}>
          {healthData?.diagnosis.details}
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={6}
          placeholder="Add notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          slotProps={{
            input: { sx: { fontSize: '0.85rem', lineHeight: 1.6 } },
          }}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              sx={{
                color: '#1976D2',
                '&.Mui-checked': {
                  color: '#1976D2',
                },
              }}
            />
          }
          label={
            <Typography variant="body2">
              I have verified all reported issues and confirm that this asset is affected.
            </Typography>
          }
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={!confirmed}
          onClick={handleRequestApproval}
          sx={{
            backgroundColor: '#2E7D32',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              backgroundColor: '#1B5E20',
            },
            '&:disabled': {
              backgroundColor: '#E0E0E0',
              color: '#9E9E9E',
            },
          }}
        >
          Request Approval
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setScenario('incorrect')}
          sx={{
            borderColor: '#212121',
            color: '#212121',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              borderColor: '#000',
              backgroundColor: '#F5F5F5',
            },
          }}
        >
          Incorrect Agent Assessment ?
        </Button>
      </Box>
    </Box>
  );

  const renderIncorrectResult = () => (
    <Box>
      <IconButton
        onClick={handleClose}
        sx={{ position: 'absolute', right: 16, top: 16 }}
      >
        <Close />
      </IconButton>

      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <DangerHexagon style={{ fontSize: 50, color: '#C62828' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Incorrect Assessment
        </Typography>
        <Typography variant="body2" sx={{ color: '#757575', mb: 3 }}>
          Submit a thorough explanation outlining why the assumption is inaccurate.
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ color: '#424242', mb: 2 }}>
          The physical inspection did not reveal any concerns, indicating the agent's assumption was wrong.
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={5}
          placeholder="Explain why the assessment is incorrect..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              sx={{
                color: '#1976D2',
                '&.Mui-checked': {
                  color: '#1976D2',
                },
              }}
            />
          }
          label={
            <Typography variant="body2">
              I have verified all reported issues and confirm that this asset is not affected.
            </Typography>
          }
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={!confirmed || !notes.trim()}
          onClick={handleSubmitIncorrect}
          sx={{
            backgroundColor: '#2E7D32',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              backgroundColor: '#1B5E20',
            },
            '&:disabled': {
              backgroundColor: '#E0E0E0',
              color: '#9E9E9E',
            },
          }}
        >
          Submit
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleClose}
          sx={{
            borderColor: '#212121',
            color: '#212121',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              borderColor: '#000',
              backgroundColor: '#F5F5F5',
            },
          }}
        >
          ← Go back
        </Button>
      </Box>
    </Box>
  );

  const renderAiAnalysisDialog = () => {
    const analysis = healthData?.aiAnalysisDetails;
    if (!analysis) return null;

    const isHealthy = scenario === 'healthy';

    return (
      <Dialog
        open={showAiAnalysis}
        onClose={() => setShowAiAnalysis(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 0,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: isHealthy
              ? 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)'
              : 'linear-gradient(135deg, #B71C1C 0%, #C62828 100%)',
            color: '#fff',
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ opacity: 0.85, mb: 0.5 }}>
              AI Analysis Report
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {healthData?.diagnosis.title}
            </Typography>
          </Box>
          <IconButton onClick={() => setShowAiAnalysis(false)} sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Confidence & Model */}
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242' }}>
                  Confidence Score
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: confidenceColor }}>
                  {analysis.confidence}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={analysis.confidence}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#E0E0E0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: confidenceColor,
                  },
                }}
              />
            </Box>
          </Box> */}

          {/* <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
            <Chip
              label={analysis.model}
              size="small"
              sx={{ backgroundColor: '#E3F2FD', color: '#1565C0', fontWeight: 500 }}
            />
            <Chip
              label={analysis.analysisTimestamp}
              size="small"
              sx={{ backgroundColor: '#F5F5F5', color: '#616161', fontWeight: 500 }}
            />
          </Box>

          <Divider sx={{ mb: 2.5 }} /> */}

          {/* Summary */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212121', mb: 1 }}>
              Summary
            </Typography>
            <Typography variant="body2" sx={{ color: '#616161', lineHeight: 1.6 }}>
              {analysis.summary}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* Key Findings */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212121', mb: 1.5 }}>
              Key Findings
            </Typography>
            {analysis.findings.map((finding, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  py: 1,
                  borderBottom: index < analysis.findings.length - 1 ? '1px solid #F5F5F5' : 'none',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#424242', minWidth: 200, flexShrink: 0 }}
                >
                  {finding.label}
                </Typography>
                <Typography variant="body2" sx={{ color: '#616161' }}>
                  {finding.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* Contributing Factors */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212121', mb: 1.5 }}>
              Contributing Factors
            </Typography>
            {analysis.contributingFactors.map((factor, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: isHealthy ? '#2E7D32' : '#C62828',
                    mt: 0.8,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ color: '#616161', lineHeight: 1.6 }}>
                  {factor}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* AI Notes */}
          {analysis.aiNotes && analysis.aiNotes.length > 0 && (
            <>
              <Divider sx={{ mb: 2.5 }} />
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212121', mb: 1.5 }}>
                  AI Notes for Technician
                </Typography>
                {analysis.aiNotes.map((note, index) => {
                  const noteConfig = {
                    warning: { icon: <ErrorOutline sx={{ fontSize: 18 }} />, bg: '#FFF3E0', border: '#FFE0B2', color: '#E65100' },
                    action: { icon: <BuildCircle sx={{ fontSize: 18 }} />, bg: '#E3F2FD', border: '#BBDEFB', color: '#1565C0' },
                    info: { icon: <InfoOutlined sx={{ fontSize: 18 }} />, bg: '#F5F5F5', border: '#E0E0E0', color: '#616161' },
                  };
                  const config = noteConfig[note.type] || noteConfig.info;

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        p: 1.5,
                        mb: 1,
                        borderRadius: '8px',
                        backgroundColor: config.bg,
                        border: `1px solid ${config.border}`,
                      }}
                    >
                      <Box sx={{ color: config.color, mt: 0.2, flexShrink: 0 }}>
                        {config.icon}
                      </Box>
                      <Typography variant="body2" sx={{ color: config.color, lineHeight: 1.6 }}>
                        {note.text}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>

        {/* Footer */}
        <Box sx={{ px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setShowAiAnalysis(false)}
            sx={{
              backgroundColor: '#212121',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#000' },
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    );
  };

  const renderContent = () => {
    if (stage === 'checking') {
      return renderLoadingScreen('Checking System Health', 'Verifying Sensors data');
    }

    if (stage === 'diagnosing') {
      return renderLoadingScreen('Diagnosing', 'Verifying ML Records');
    }

    if (stage === 'result') {
      if (scenario === 'incorrect') {
        return renderIncorrectResult();
      }
      if (scenario === 'healthy') {
        return renderHealthyResult();
      }
      return renderFailureResult();
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: 3,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {renderContent()}
      </DialogContent>
      {renderAiAnalysisDialog()}
    </Dialog>
  );
};

export default HealthCheckModal;